/**
 * LGPD Block C — best-effort bridge from the local consent manager (Block B)
 * to the backend consent log.
 *
 * Design contract (do not violate):
 *   - The LOCAL record (consentManager.ts) stays the single source of truth for
 *     immediate UI gating. This module only MIRRORS decisions to the backend
 *     for server-side LGPD evidence.
 *   - Every call is fire-and-forget and swallows all errors. A backend failure
 *     must never throw into a click handler, break the app, or change gating.
 *   - This module loads NO analytics/marketing scripts. Logging success does
 *     not enable any tracker — that remains gated by scriptLoader.ts only.
 *   - The only thing persisted locally is a random, NON-personal correlation id
 *     (``rager_consent_anon_id``) so a pre-login browser's consent events can be
 *     tied together server-side. It contains no personal data.
 */

import { privacyApi, type ConsentBackendSource } from '@/shared/api/privacyApi'
import { CONSENT_POLICY_VERSION, type ConsentPreferences } from './consentManager'

export type { ConsentBackendSource }

/** localStorage key holding the random anonymous correlation id (non-PII). */
export const CONSENT_ANON_ID_KEY = 'rager_consent_anon_id'

function generateAnonId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the non-crypto fallback
  }
  // Fallback for old browsers: still random + non-personal.
  return `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

/**
 * Read (or lazily create) the local anonymous correlation id. Returns
 * ``undefined`` when storage is unavailable (SSR / private mode) — callers then
 * simply omit it and the backend logs an anonymous event with no correlation.
 */
export function getOrCreateAnonId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const existing = window.localStorage.getItem(CONSENT_ANON_ID_KEY)
    if (existing && existing.length > 0) return existing
    const fresh = generateAnonId()
    window.localStorage.setItem(CONSENT_ANON_ID_KEY, fresh)
    return fresh
  } catch {
    return undefined
  }
}

/**
 * Mirror a just-stored consent decision to the backend. Best-effort: returns
 * immediately and never rejects. ``essential`` is always reported as ``true``
 * (the server forces it regardless).
 */
export function syncConsentToBackend(
  record: ConsentPreferences,
  source: ConsentBackendSource,
): void {
  try {
    const anonymousId = getOrCreateAnonId()
    void privacyApi
      .postConsent({
        policyVersion: record.policyVersion,
        action: record.action,
        categories: {
          essential: true,
          analytics: record.categories.analytics,
          marketing: record.categories.marketing,
        },
        source,
        anonymousId,
      })
      .catch(() => {
        // Best-effort: swallow. Local record already governs the UI.
      })
  } catch {
    // Never let evidence logging affect the caller.
  }
}

/**
 * Mirror a consent revocation to the backend. Best-effort; appends a ``revoke``
 * event server-side (the backend never deletes prior events). The local effect
 * (clearing the stored decision) is the caller's responsibility.
 */
export function syncRevokeToBackend(source: ConsentBackendSource): void {
  try {
    const anonymousId = getOrCreateAnonId()
    void privacyApi
      .revokeConsent({
        policyVersion: CONSENT_POLICY_VERSION,
        source,
        anonymousId,
      })
      .catch(() => {
        // Best-effort: swallow.
      })
  } catch {
    // Never let evidence logging affect the caller.
  }
}
